import { useState } from "react"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { Input } from "@base-ui/react/input"
import { toast } from "sonner"
import {
  AuthShell,
  OtpInput,
  PrimaryButton,
  TextLink,
  fieldCls,
  labelCls,
} from "@/components/auth/AuthShell"
import { requestPasswordResetOtp, confirmPasswordReset } from "@/core/auth"

// Standalone set-a-password flow (OTP-gated). The PREFERRED day-one path is a
// passkey (offered on the accept-invitation screen); this is the fallback for
// people who'd rather use a password, and the canonical reset path. Step 1
// emails a code; step 2 sets the password. Reachable directly at /set-password
// (e.g. ?email=…), and linked from the post-accept "Secure your account" step.
export const Route = createFileRoute("/set-password")({
  validateSearch: (search: Record<string, unknown>): { email?: string } => ({
    email: typeof search.email === "string" ? search.email : undefined,
  }),
  component: SetPassword,
})

function SetPassword() {
  const navigate = useNavigate()
  const { email: emailFromUrl } = Route.useSearch()
  const [stage, setStage] = useState<"email" | "verify">("email")
  const [email, setEmail] = useState(emailFromUrl?.trim() ?? "")
  const emailLocked = Boolean(emailFromUrl?.trim())
  const [otp, setOtp] = useState("")
  const [password, setPassword] = useState("")
  const [confirm, setConfirm] = useState("")
  const [busy, setBusy] = useState(false)

  async function onSendCode(e: React.FormEvent) {
    e.preventDefault()
    if (!email) return
    setBusy(true)
    try {
      await requestPasswordResetOtp(email)
      toast.success(`Code sent to ${email}`)
      setStage("verify")
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't send code")
    } finally {
      setBusy(false)
    }
  }

  async function onConfirm(e: React.FormEvent) {
    e.preventDefault()
    if (!email || otp.length !== 6 || password.length < 8) return
    if (password !== confirm) {
      toast.error("Passwords don't match")
      return
    }
    setBusy(true)
    try {
      await confirmPasswordReset(email, otp, password)
      toast.success("Password set — you can sign in with your email + password.")
      await navigate({ to: "/", replace: true })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't set password")
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthShell title="Set a password">
      {stage === "email" ? (
        <form onSubmit={onSendCode} className="space-y-4">
          <div>
            <label htmlFor="sp-email" className={`${labelCls} text-center`}>Email</label>
            <Input
              id="sp-email"
              type="email"
              autoComplete="email"
              autoFocus={!emailLocked}
              readOnly={emailLocked}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className={fieldCls}
            />
            <p className="mt-2 text-center text-[12px] text-white/55">
              We'll email a 6-digit code to confirm it's you.
            </p>
          </div>
          <PrimaryButton disabled={busy || !email}>
            {busy ? "Sending…" : "Send code"}
          </PrimaryButton>
        </form>
      ) : (
        <form onSubmit={onConfirm} className="space-y-4">
          <div>
            <label className={`${labelCls} text-center`}>Enter your code</label>
            <OtpInput value={otp} onChange={setOtp} autoFocus />
            <p className="mt-2 text-center text-[12px] text-white/55">
              Sent to <span className="font-medium text-white/80">{email}</span>
            </p>
          </div>
          <div>
            <label htmlFor="sp-pw" className={`${labelCls} text-center`}>New password</label>
            <Input
              id="sp-pw"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className={fieldCls}
            />
          </div>
          <div>
            <label htmlFor="sp-pw2" className={`${labelCls} text-center`}>Confirm password</label>
            <Input
              id="sp-pw2"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              className={fieldCls}
            />
            <p className="mt-2 text-center text-[12px] text-white/55">At least 8 characters.</p>
          </div>
          <PrimaryButton disabled={busy || otp.length !== 6 || password.length < 8}>
            {busy ? "Saving…" : "Set password"}
          </PrimaryButton>
          <div className="flex items-center justify-between">
            <TextLink onClick={() => setStage("email")}>← Back</TextLink>
            <TextLink onClick={() => void requestPasswordResetOtp(email)}>Resend code</TextLink>
          </div>
        </form>
      )}
    </AuthShell>
  )
}
